// Входът е врата с две крила: накъде гледа нов вход и мерките на рисунката
// (`EntranceDoor`). Чисти функции — ползват ги редакторът и рисуването.

import type { SchemaElement } from "../../api/restaurantSchema";
import type { LogicalBounds } from "./geometry";

// Вход, пуснат на до толкова единици (1,5 м) от ръба на залата (или извън
// нея), застава с гръб към него — вратата се отваря навътре към залата.
const EDGE_FACING_DISTANCE = 150;

type Edge = "top" | "right" | "bottom" | "left";

// При 0° прагът е отдолу и крилата се отварят нагоре (врата на долния ръб).
const ROTATION_FROM_EDGE: Record<Edge, number> = { bottom: 0, left: 90, top: 180, right: 270 };

/**
 * Завъртането на НОВ вход в `position`: с гръб към най-близкия ръб на залата
 * (`room` — рамката на останалите елементи), ако е до него или извън нея; по
 * средата на залата — 0°. При равно разстояние (ъгъл) печели горният/долният
 * ръб пред страничния. Само при създаване — после входът се върти ръчно като
 * всеки обект.
 */
export const entranceRotationAt = (
  position: Pick<SchemaElement, "x" | "y">,
  room: LogicalBounds,
) => {
  // Отрицателно разстояние = извън залата от тази страна.
  const distances: [Edge, number][] = [
    ["top", position.y - room.top],
    ["bottom", room.bottom - position.y],
    ["left", position.x - room.left],
    ["right", room.right - position.x],
  ];
  const [edge, distance] = distances.sort((first, second) => first[1] - second[1])[0];
  return distance > EDGE_FACING_DISTANCE ? 0 : ROTATION_FROM_EDGE[edge];
};

// ── Рисунката (в пиксели на дизайна) ────────────────────────────────────────

/** Дебелината на касите и на прага — като вътрешна стена с малко запас. */
const JAMB = 8;
/** Колко навътре от прага стига касата. */
const JAMB_RISE = 9;
// Луфт между върховете на двете затворени крила.
const LEAF_GAP = 2;
// Колко са отворени крилата (90° = докрай, опрени в касите).
const LEAF_OPEN_DEGREES = 75;
// Колко остава между етикета върху прага и всяка от касите.
const LABEL_CLEARANCE = 4;

/**
 * Мерките на вратата в кутия `width` × `height` (пиксели на дизайна), при 0°:
 * прагът е долната ивица между двете каси, пантите са в горните му ъгли, а
 * всяко крило е отсечка от пантата до върха си. `closedX` е къде стига върхът
 * на затвореното крило — оттам тръгва дъгата му.
 */
export const doorGeometry = (width: number, height: number) => {
  const sill = height - JAMB;
  const leaf = width / 2 - JAMB - LEAF_GAP;
  const radians = (LEAF_OPEN_DEGREES * Math.PI) / 180;
  const reach = Math.cos(radians) * leaf;
  const rise = Math.sin(radians) * leaf;
  return {
    jamb: JAMB,
    jambHeight: JAMB + JAMB_RISE,
    sill,
    // Оста на прага — върху нея стои етикетът.
    sillCenter: sill + JAMB / 2,
    leaf,
    // Лявото крило и огледалното му дясно.
    leaves: [
      { hingeX: JAMB, tipX: JAMB + reach, closedX: JAMB + leaf, clockwise: false },
      { hingeX: width - JAMB, tipX: width - JAMB - reach, closedX: width - JAMB - leaf, clockwise: true },
    ],
    tipY: sill - rise,
    // Мястото между касите — толкова широк етикет се събира върху прага при 0°.
    labelRoom: width - 2 * (JAMB + LABEL_CLEARANCE),
  };
};

// ── Етикетът „Вход" върху прага ─────────────────────────────────────────────

// В пиксели на дизайна: височина, шрифт, отстъп встрани и иконата с
// разстоянието до текста (в em).
const LABEL_HEIGHT = 26;
const LABEL_FONT = 12.5;
const LABEL_PADDING = 9;
const LABEL_ICON_EM = 1.45;
// Под толкова пиксела на екрана надписът не се чете — не се смалява повече,
// а редът му иска поне LABEL_LINE пъти шрифта на височина.
const MIN_LABEL_FONT_PX = 10;
const LABEL_LINE = 1.4;

/**
 * Етикетът в пиксели на ЕКРАНА. Надписът се показва, ако се събира между
 * касите (при четим шрифт); иначе — малък вход, по-дълга дума, смалена схема —
 * остава само иконата, която се мащабира с вратата. Етикетът е изправен, а
 * вратата — завъртяна: при 0° лежи върху прага; колкото по-дълъг става
 * напречно на прага, толкова се дърпа навътре (`centerY`) — навън от вратата
 * се подава най-много колкото при 0°.
 */
export const doorLabelLayout = ({
  textEm,
  rotation,
  labelRoom,
  sillCenter,
  designPixel,
}: {
  // Ширината на надписа в em (`textWidthEm`).
  textEm: number;
  rotation: number;
  // От `doorGeometry`, в пиксели на дизайна.
  labelRoom: number;
  sillCenter: number;
  // Екранни пиксели за един пиксел на дизайна.
  designPixel: number;
}) => {
  const textFont = Math.max(MIN_LABEL_FONT_PX, LABEL_FONT * designPixel);
  const textWidth = (textEm + LABEL_ICON_EM) * textFont + LABEL_PADDING * 2 * designPixel;
  const showText = textWidth <= labelRoom * designPixel;
  const iconSide = LABEL_HEIGHT * designPixel;
  const width = showText ? textWidth : iconSide;
  const height = showText ? Math.max(iconSide, textFont * LABEL_LINE) : iconSide;
  const radians = (rotation * Math.PI) / 180;
  const halfAcross = (width * Math.abs(Math.sin(radians)) + height * Math.abs(Math.cos(radians))) / 2;
  return {
    showText,
    fontSize: showText ? textFont : LABEL_FONT * designPixel,
    height,
    paddingInline: showText ? LABEL_PADDING * designPixel : 0,
    centerY: sillCenter * designPixel - Math.max(0, halfAcross - height / 2),
  };
};
